import { memo } from "react";
import { Calendar, CalendarPlus, Share2 } from "lucide-react";
import { toast } from "sonner";
import type { GoldenSerenityComponentProps } from "./types";
import { GS, formatDate } from "./utils";
import { AmbientGlow, GoldHairline } from "./decorations";
import { buildGoogleCalendarUrl, downloadIcsFile } from "@/utils/saveTheDate";

type GoldenSerenitySaveTheDateProps = GoldenSerenityComponentProps & {
  onShare?: () => Promise<void> | void;
};

export const GoldenSerenitySaveTheDate = memo(
  ({ event, config, locale, t, onShare }: GoldenSerenitySaveTheDateProps) => {
    if (!event.date) return null;

    const includesWedding = event.includesWedding ?? true;
    const includesBaptism = event.includesBaptism ?? false;
    const isBaptismOnly = includesBaptism && !includesWedding;
    const descriptionKey = isBaptismOnly
      ? "events.form.template.saveTheDateDescriptionBaptism"
      : "events.form.template.saveTheDateDescription";

    const handleGoogleCalendar = () => {
      const url = buildGoogleCalendarUrl(event, locale);
      if (url) {
        window.open(url, "_blank", "noopener,noreferrer");
      }
    };

    const handleAppleCalendar = () => {
      downloadIcsFile(event, locale);
    };

    const handleShare = async () => {
      if (onShare) {
        await onShare();
        return;
      }
      if (navigator.share) {
        try {
          await navigator.share({ title: event.name, url: window.location.href });
        } catch {
          // User cancelled the share sheet — nothing to do.
        }
      } else {
        await navigator.clipboard.writeText(window.location.href);
        toast.success(t("common.copied"));
      }
    };

    const buttonClassName =
      "w-full sm:w-auto inline-flex items-center justify-center gap-2 px-8 sm:px-10 py-3.5 rounded-full text-sm font-semibold tracking-wide transition-all duration-500 hover:shadow-[0_6px_20px_rgba(201,168,106,0.25)]";
    const buttonStyle = {
      background: GS.gold,
      // JUB-90: creamLight-on-gold was 2.1:1 (fails WCAG AA); GS.ink gives ~5.7:1
      color: GS.ink,
      fontFamily: config.typography.body,
    };

    return (
      <section
        id="save-the-date"
        className="relative py-20 sm:py-28 overflow-hidden"
        style={{ background: `linear-gradient(to bottom, ${GS.creamDeep}, ${GS.creamLight})` }}
      >
        <AmbientGlow />

        <div className="max-w-5xl mx-auto px-4 sm:px-6 relative z-10">
          {/* Header card */}
          <div className="max-w-2xl mx-auto mb-10 sm:mb-14">
            <div
              className="relative p-8 sm:p-12 md:p-16 text-center rounded-2xl border"
              style={{
                background: `linear-gradient(to bottom right, ${GS.creamLight}, ${GS.creamDeep})`,
                borderColor: "rgba(232,220,200,0.4)",
                boxShadow: "0 12px 48px rgba(139,115,85,0.15)",
              }}
            >
              <div className="space-y-6">
                <div className="flex items-center justify-center gap-3 mb-6">
                  <Calendar className="w-6 h-6" style={{ color: GS.gold }} aria-hidden="true" />
                </div>

                <h2
                  className="text-3xl sm:text-5xl font-medium"
                  style={{ color: config.palette.text, fontFamily: config.typography.heading }}
                >
                  {t("events.form.template.saveTheDate")}
                </h2>

                <div className="space-y-3">
                  <p
                    className="text-xl sm:text-2xl font-medium"
                    style={{ color: config.palette.text, fontFamily: config.typography.heading }}
                  >
                    {formatDate(event.date, locale, {
                      weekday: "long",
                      day: "numeric",
                      month: "long",
                      year: "numeric",
                    })}
                  </p>
                  <GoldHairline />
                </div>

                <p
                  className="text-sm leading-relaxed max-w-md mx-auto"
                  style={{ color: config.palette.mutedText, fontFamily: config.typography.body }}
                >
                  {t(descriptionKey)}
                </p>
              </div>
            </div>
          </div>

          {/* Calendar buttons */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 max-w-3xl mx-auto">
            <button
              type="button"
              className={buttonClassName}
              style={buttonStyle}
              onClick={handleGoogleCalendar}
              aria-label={t("events.form.template.addToGoogleCalendar")}
              title={t("events.form.template.addToGoogleCalendar")}
            >
              <Calendar className="h-4 w-4" aria-hidden="true" />
              Google Calendar
            </button>
            <button
              type="button"
              className={buttonClassName}
              style={buttonStyle}
              onClick={handleAppleCalendar}
              aria-label={t("events.form.template.downloadCalendarFile")}
              title={t("events.form.template.downloadCalendarFile")}
            >
              <CalendarPlus className="h-4 w-4" aria-hidden="true" />
              Apple Calendar
            </button>
            <button
              type="button"
              className={buttonClassName}
              style={buttonStyle}
              onClick={handleShare}
              aria-label={t("events.form.template.share")}
            >
              <Share2 className="h-4 w-4" aria-hidden="true" />
              {t("events.form.template.share")}
            </button>
          </div>

        </div>
      </section>
    );
  },
);

GoldenSerenitySaveTheDate.displayName = "GoldenSerenitySaveTheDate";
